/**
 * Rights Tab Component
 *
 * Displays list of rights (permissions) with CRUD operations
 * Features: DataTable, search, create, edit, delete
 */

import { useState, useMemo, useCallback } from 'react';
import { IconKey, IconEdit, IconTrash } from '@tabler/icons-react';
import { Stack, Group, Text, Badge, ActionIcon } from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { alert } from '../../../../components/ui/providers/AlertProvider';
import type { RightsTabProps, RightRow } from '../types';

export function RightsTab({ rights, onRefresh, onEdit, onDelete, loading }: RightsTabProps) {
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * Handle delete with confirmation
   */
  const handleDeleteClick = useCallback(
    async (right: RightRow) => {
      const confirmed = await alert.confirm(
        'Delete Right',
        `Are you sure you want to delete the right "${right.name}"? This will remove it from all roles. This action cannot be undone.`
      );
      if (!confirmed) return;

      onDelete(right);
    },
    [onDelete]
  );

  /**
   * Filter rights by search query
   */
  const filteredRights = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') return rights;

    const query = searchQuery.toLowerCase().trim();
    return rights.filter((right) => {
      const name = (right.name || '').toLowerCase();
      const description = (right.description || '').toLowerCase();
      const id = String(right.id).toLowerCase();
      return name.includes(query) || description.includes(query) || id.includes(query);
    });
  }, [rights, searchQuery]);

  /**
   * Convert to DataTable row format
   */
  const rightRows: RightRow[] = useMemo(() => {
    return filteredRights.map((right) => ({
      ...right,
      id: right.id, // Ensure id is non-nullable
    }));
  }, [filteredRights]);

  /**
   * Define DataTable columns
   */
  const columns: DataTableColumn<RightRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Right Name',
        sortable: true,
        render: (value) => (
          <Group gap="xs">
            <IconKey size={16} style={{ opacity: 0.6 }} />
            <Text fw={500} size="sm" style={{ fontFamily: 'monospace' }}>
              {value}
            </Text>
          </Group>
        ),
      },
      {
        key: 'description',
        label: 'Description',
        render: (value) => (
          <Text size="sm" c="dimmed">
            {value || '-'}
          </Text>
        ),
      },
      {
        key: 'id',
        label: 'ID',
        sortable: true,
        render: (value) => (
          <Badge size="sm" variant="light" color="gray">
            {value}
          </Badge>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (_, right) => (
          <Group gap="xs" justify="flex-end">
            <ActionIcon
              variant="light"
              size="sm"
              color="gray"
              onClick={() => onEdit(right)}
              aria-label="Edit right"
            >
              <IconEdit size={14} />
            </ActionIcon>
            <ActionIcon
              variant="light"
              size="sm"
              color="red"
              onClick={() => handleDeleteClick(right)}
              aria-label="Delete right"
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        ),
      },
    ],
    [onEdit, handleDeleteClick]
  );

  return (
    <Stack gap="md">
      <DataTable<RightRow>
        data={rightRows}
        columns={columns}
        searchPlaceholder="Search rights by name, description, or ID..."
        searchValue={searchQuery}
        onSearch={(query) => setSearchQuery(query)}
        gradientVariant="primary"
        highlightOnHover
        striped
        withRowAnimations
        enableInternalSort
        emptyMessage={loading ? 'Loading rights...' : 'No rights found'}
        loading={loading}
      />
    </Stack>
  );
}
